import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { Save, RotateCcw, DollarSign } from 'lucide-react';

// Default pricing structure
export const DEFAULT_PRICING = {
  private: {
    trial: { price: 450, hours: 1, label: 'Trial', perHour: 450 },
    '10hr': { price: 5500, hours: 10, label: '10 hours', perHour: 550 },
    '20hr': { price: 10000, hours: 20, label: '20 hours', perHour: 500, discount: '9%' },
    '30hr': { price: 13500, hours: 30, label: '30 hours', perHour: 450, discount: '18%' },
  },
  group: {
    starter: { price: 3000, classes: 10, label: 'Starter', perClass: 300 },
  },
};

const PricingManager = () => {
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadPricing();
  }, []);

  const loadPricing = async () => {
    try {
      setLoading(true);
      const settings = await api.admin.getSettings();
      if (settings?.pricing) {
        const parsed = typeof settings.pricing === 'string' 
          ? JSON.parse(settings.pricing) 
          : settings.pricing;
        // Merge with defaults to ensure all fields exist
        setPricing({
          private: {
            trial: { ...DEFAULT_PRICING.private.trial, ...(parsed.private?.trial || {}) },
            '10hr': { ...DEFAULT_PRICING.private['10hr'], ...(parsed.private?.['10hr'] || {}) },
            '20hr': { ...DEFAULT_PRICING.private['20hr'], ...(parsed.private?.['20hr'] || {}) },
            '30hr': { ...DEFAULT_PRICING.private['30hr'], ...(parsed.private?.['30hr'] || {}) },
          },
          group: {
            starter: { ...DEFAULT_PRICING.group.starter, ...(parsed.group?.starter || {}) },
          },
        });
      }
    } catch (err) {
      console.error('Error loading pricing:', err);
      // Use defaults
    } finally {
      setLoading(false);
    }
  };

  const handlePrivateChange = (tier, field, value) => {
    const numValue = parseInt(value) || 0;
    setPricing(prev => ({
      ...prev,
      private: {
        ...prev.private,
        [tier]: {
          ...prev.private[tier],
          [field]: numValue,
          // Auto-calculate per-hour rate
          ...(field === 'price' && prev.private[tier].hours
            ? { perHour: Math.round(numValue / prev.private[tier].hours) }
            : {}),
        },
      },
    }));
  };

  const handleGroupChange = (tier, field, value) => {
    const numValue = parseInt(value) || 0;
    setPricing(prev => ({
      ...prev,
      group: {
        ...prev.group,
        [tier]: {
          ...prev.group[tier],
          [field]: numValue,
          // Auto-calculate per-class rate
          ...(field === 'price' && prev.group[tier].classes
            ? { perClass: Math.round(numValue / prev.group[tier].classes) }
            : {}),
        },
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      // Send pricing as part of the settings object
      // The backend merges this with existing settings (notificationEmail, etc.)
      const response = await api.admin.updateSettings({
        pricing: pricing,
      });

      if (response.success) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        throw new Error(response.message || 'Failed to save pricing');
      }
    } catch (err) {
      console.error('Error saving pricing:', err);
      setError(err.message || 'Failed to save pricing');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setPricing(DEFAULT_PRICING);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <svg className="animate-spin h-8 w-8 text-purple-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-3 bg-red-900 bg-opacity-20 border border-red-700 rounded text-red-400 text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="p-3 bg-green-900 bg-opacity-20 border border-green-700 rounded text-green-400 text-sm">
          Pricing saved successfully! Changes will appear on the website immediately.
        </div>
      )}

      {/* Private Tutor Pricing */}
      <div>
        <h4 className="text-md font-semibold text-white mb-1 flex items-center gap-2">
          <DollarSign size={18} className="text-purple-400" />
          Private Tutor Pricing
        </h4>
        <p className="text-xs text-gray-400 mb-4">
          Set prices in HKD. Per-hour rates are calculated automatically.
        </p>

        <div className="space-y-3">
          {Object.entries(pricing.private).map(([tier, data]) => (
            <div
              key={tier}
              className="bg-gray-900 rounded-lg p-4 border border-gray-700"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="sm:w-32 flex-shrink-0">
                  <span className="text-sm font-medium text-gray-300">
                    {data.label}
                  </span>
                  <span className="text-xs text-gray-500 block">
                    {data.hours} hour{data.hours > 1 ? 's' : ''}
                  </span>
                </div>

                <div className="flex items-center gap-2 flex-1">
                  <span className="text-gray-400 text-sm">HKD</span>
                  <input
                    type="number"
                    value={data.price}
                    onChange={(e) => handlePrivateChange(tier, 'price', e.target.value)}
                    className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm text-white w-32 focus:border-purple-500 focus:outline-none"
                    min="0"
                    step="50"
                  />
                </div>

                <div className="text-xs text-gray-400 sm:w-32 text-right">
                  HKD {data.perHour}/hr
                  {data.discount && (
                    <span className="text-purple-400 ml-1">
                      • Save {data.discount}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Group Class Pricing */}
      <div>
        <h4 className="text-md font-semibold text-white mb-1 flex items-center gap-2">
          <DollarSign size={18} className="text-purple-400" />
          Group Class Pricing
        </h4>
        <p className="text-xs text-gray-400 mb-4">
          Set prices in HKD. Per-class rates are calculated automatically.
        </p>

        <div className="space-y-3">
          {Object.entries(pricing.group).map(([tier, data]) => (
            <div
              key={tier}
              className="bg-gray-900 rounded-lg p-4 border border-gray-700"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="sm:w-32 flex-shrink-0">
                  <span className="text-sm font-medium text-gray-300">
                    {data.label}
                  </span>
                  <span className="text-xs text-gray-500 block">
                    {data.classes} classes
                  </span>
                </div>

                <div className="flex items-center gap-2 flex-1">
                  <span className="text-gray-400 text-sm">HKD</span>
                  <input
                    type="number"
                    value={data.price}
                    onChange={(e) => handleGroupChange(tier, 'price', e.target.value)}
                    className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm text-white w-32 focus:border-purple-500 focus:outline-none"
                    min="0"
                    step="50"
                  />
                </div>

                <div className="text-xs text-gray-400 sm:w-32 text-right">
                  HKD {data.perClass}/class
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center pt-4 border-t border-gray-700">
        <Button
          type="button"
          variant="ghost"
          onClick={handleReset}
          className="text-gray-400 hover:text-white flex items-center"
        >
          <RotateCcw size={14} className="mr-2" />
          Reset to Defaults
        </Button>

        <Button
          type="button"
          variant="primary"
          onClick={handleSave}
          isLoading={saving}
          className="bg-purple-600 hover:bg-purple-700 flex items-center"
        >
          <Save size={16} className="mr-2" />
          Save Pricing
        </Button>
      </div>
    </div>
  );
};

export default PricingManager;